"use client";

import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function formatPrefix(i: number) {
  return (i + 1).toString().padStart(2, "0");
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-8 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i} className="group">
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative inline-flex items-center px-3 py-2 text-xs font-default font-medium uppercase tracking-[0.05em] text-foreground hover:bg-transparent focus:bg-transparent hover:text-primary transition-colors">
                <span className="font-mono text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mr-1.5">
                  {formatPrefix(i)}/
                </span>
                <span>{item.label}</span>
                <span className="absolute left-3 right-3 -bottom-0.5 h-[2px] bg-primary scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 px-6">
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-10 w-full bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col px-6">
      {menuItems.map((item, i) => <SheetClose asChild key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="group flex items-baseline gap-3 border-b border-border py-5 text-foreground hover:text-primary transition-colors">
            <span className="font-mono text-xs text-muted-foreground">
              {formatPrefix(i)}/
            </span>
            <span className="text-2xl font-default font-medium uppercase tracking-[0.02em]">
              {item.label}
            </span>
          </Link>
        </SheetClose>)}
    </nav>;
}
export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [time, setTime] = useState("");
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const hh = now.getUTCHours().toString().padStart(2, "0");
      const mm = now.getUTCMinutes().toString().padStart(2, "0");
      const ss = now.getUTCSeconds().toString().padStart(2, "0");
      setTime(`UTC ${hh}:${mm}:${ss}`);
    };
    updateTime();
    const id = window.setInterval(updateTime, 1000);
    return () => window.clearInterval(id);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="4624" id="navigation" className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${scrolled ? "bg-background/70 backdrop-blur-lg border-border" : "bg-background border-border"} text-foreground`}>
      <div className="mx-auto flex h-[72px] w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 flex-1">
          <Link to="/" className="flex items-center">
            <WvcLogo className="h-6 w-auto" />
          </Link>
          <span className="hidden lg:inline-block font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground border-l border-border pl-3 ml-1">
            OLDENBURG / DIGITAL
          </span>
        </div>

        <div className="hidden lg:flex items-center justify-center flex-1">
          <MenuProvider menu_id="16">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="hidden lg:flex items-center justify-end gap-4 flex-1">
          <span className="font-mono text-xs text-muted-foreground tabular-nums">
            {time}
          </span>
          <Button asChild className="h-10 bg-primary text-primary-foreground font-default font-medium text-xs uppercase tracking-[0.1em] px-4 hover:bg-primary/90">
            <Link to="/ContactPage">ANFRAGEN →</Link>
          </Button>
        </div>

        <div className="flex lg:hidden items-center justify-end flex-1">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Menü öffnen" className="text-foreground hover:bg-accent">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md bg-background text-foreground border-l border-border p-0 flex flex-col">
              <SheetTitle className="sr-only">Navigation</SheetTitle>

              <div className="flex items-center justify-between px-6 h-[72px] border-b border-border">
                <SheetClose asChild>
                  <Link to="/" className="flex items-center">
                    <WvcLogo className="h-6 w-auto" />
                  </Link>
                </SheetClose>
                <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                  ORVO WEB
                </span>
              </div>

              <div className="flex-1 overflow-y-auto py-6">
                <div className="px-6 mb-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                    [ MENÜ ]
                  </span>
                </div>
                <MenuProvider menu_id="16">
                  <MobileMenuConsumer />
                </MenuProvider>

                <div className="px-6 mt-8">
                  <SheetClose asChild>
                    <Button asChild className="w-full h-12 bg-primary text-primary-foreground font-default font-medium text-xs uppercase tracking-[0.1em]">
                      <Link to="/ContactPage">ANFRAGEN →</Link>
                    </Button>
                  </SheetClose>
                </div>
              </div>

              <div className="border-t border-border px-6 py-4 flex items-center justify-between">
                <span className="font-mono text-xs text-muted-foreground tabular-nums">
                  {time}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                  OLDENBURG / GERMANY
                </span>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}